/**
 * Review — the things the WMS refused to guess about.
 *
 * Every source here represents a deliberate stop: a stock line that could not be
 * resolved, a COD parcel that came back short, an exchange whose correcting write
 * failed. The page exists because until now all of those landed in tables nobody
 * opened.
 *
 * Design rule, and the reason this is not just "a list of open work": ONLY
 * exceptions belong here. Normal queues (returns to process, COD cases to delete)
 * live on their own pages. If the badge is permanently lit by routine work it stops
 * meaning anything, and then a real problem sits in it unread — which is exactly
 * the failure this page was built to end.
 *   — Claude
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, authFetch } from './api';
import { PhoneCards, CardLine } from './PhoneCards';

type Source = {
  key: string;
  label: string;
  why: string;
  count: number;
  severity: 'high' | 'normal';
  link: string | null;
  link_label?: string | null;
  resolvable?: boolean;
  error?: string;
};
type Item = Record<string, any>;

const fmtDate = (v: any) =>
  !v ? '—' : String(v).slice(0, 16).replace('T', ' ');

export default function ReviewPage() {
  const navigate = useNavigate();
  const [sources, setSources] = useState<Source[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | string | null>(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/review/summary`);
      if (!r.ok) throw new Error(`summary ${r.status}`);
      const d = await r.json();
      setSources(d.sources || []);
      // Open the first source that actually has something, so the page lands on
      // the problem rather than on an index the reader has to click through.
      setOpen(o => o ?? (d.sources || []).find((s: Source) => s.count > 0)?.key ?? null);
    } catch (e: any) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!open) { setItems([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${API}/review/items?source=${encodeURIComponent(open)}`);
        if (!r.ok) throw new Error(`items ${r.status}`);
        const d = await r.json();
        if (!cancelled) setItems(d.items || []);
      } catch (e: any) {
        if (!cancelled) { setItems([]); setErr(e.message || String(e)); }
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const resolve = async (id: number | string, dismiss: boolean) => {   // W-ids are strings
    const note = window.prompt(
      dismiss
        ? 'Dismissing this — what was it? (optional)'
        : 'What did you do about it? (optional)') ?? '';
    setBusy(id);
    try {
      // Source-generic: every resolvable source exposes POST /review/{key}/{id}/resolve.
      const r = await authFetch(`${API}/review/${open}/${id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ dismiss, note }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
      setItems(x => x.filter(i => i.id !== id));
      load();
    } catch (e: any) {
      setErr(e.message || String(e));
    } finally {
      setBusy(null);
    }
  };

  const total = sources.reduce((n, s) => n + s.count, 0);
  const active = sources.find(s => s.key === open);

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-baseline gap-3 mb-1">
        <h1 className="text-xl font-semibold text-white">Review</h1>
        {!loading && (
          <span className={`text-sm ${total ? 'text-amber-400' : 'text-gray-500'}`}>
            {total ? `${total} item${total === 1 ? '' : 's'} need a person` : 'Nothing waiting'}
          </span>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-5">
        Where the WMS stopped instead of guessing. Nothing here fixes itself.
      </p>

      {err && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-red-900/30 border border-red-800 text-sm text-red-300">
          {err}
        </div>
      )}

      {loading ? (
        <div className="text-gray-500 text-sm">Loading…</div>
      ) : (
        <>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 mb-6">
            {sources.map(s => {
              const lit = s.count > 0;
              return (
                <button key={s.key} onClick={() => setOpen(s.key)}
                  className={`text-left px-3 py-3 rounded-lg border transition-colors ${
                    open === s.key ? 'border-blue-500 bg-blue-950/30'
                    : lit ? 'border-gray-700 bg-[#1a1d27] hover:border-gray-600'
                    : 'border-gray-800 bg-[#1a1d27]/50 hover:border-gray-700'
                  }`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-sm font-medium ${lit ? 'text-white' : 'text-gray-500'}`}>
                      {s.label}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      !lit ? 'bg-gray-800 text-gray-600'
                      : s.severity === 'high' ? 'bg-red-500 text-white'
                      : 'bg-amber-500 text-white'
                    }`}>{s.count}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 leading-snug">{s.why}</p>
                  {s.error && (
                    <p className="text-xs text-red-400 mt-1">Count failed: {s.error}</p>
                  )}
                </button>
              );
            })}
          </div>

          {active && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold text-white">{active.label}</h2>
                {active.link && (
                  <button onClick={() => navigate(active.link!)}
                    className="text-xs text-blue-400 hover:text-blue-300">
                    Open {active.link_label || 'the page'} →
                  </button>
                )}
              </div>
              {items.length === 0 ? (
                <div className="px-3 py-6 rounded-lg border border-gray-800 bg-[#1a1d27]/50 text-sm text-gray-500 text-center">
                  Nothing here. That is the state this should normally be in.
                </div>
              ) : (
                <PhoneCards rows={items} keyOf={it => it.id}
                  card={it => (
                    <div>
                      {Object.keys(it).filter(k => k !== 'id' && !k.startsWith('_')).map(k => (
                        <CardLine key={k} label={k.replace(/_/g, ' ')}>
                          {k.endsWith('_at') || k === 'created_at' ? fmtDate(it[k])
                            : it[k] === null || it[k] === '' ? '—' : String(it[k])}
                        </CardLine>
                      ))}
                      {(it._open || active.resolvable) && (
                        <div className="flex gap-2 mt-2">
                          {it._open && <button onClick={() => navigate(it._open)} className="text-xs px-3 py-1.5 rounded bg-blue-700 text-white">Fix →</button>}
                          {active.resolvable && <button disabled={busy === it.id} onClick={() => resolve(it.id, false)} className="text-xs px-3 py-1.5 rounded bg-green-700 text-white disabled:opacity-50">Done</button>}
                          {active.resolvable && active.key === 'stock_unresolved' && <button disabled={busy === it.id} onClick={() => resolve(it.id, true)} className="text-xs px-3 py-1.5 rounded bg-gray-700 text-gray-200 disabled:opacity-50">Dismiss</button>}
                        </div>
                      )}
                    </div>
                  )}>
                <div className="overflow-x-auto rounded-lg border border-gray-800">
                  <table className="w-full text-sm">
                    <thead className="bg-[#1a1d27] text-gray-500 text-xs uppercase tracking-wider">
                      <tr>
                        {Object.keys(items[0])
                          .filter(k => k !== 'id' && !k.startsWith('_'))
                          .map(k => (
                            <th key={k} className="text-left font-semibold px-3 py-2 whitespace-nowrap">
                              {k.replace(/_/g, ' ')}
                            </th>
                          ))}
                        {'_open' in items[0] && <th className="px-3 py-2" />}
                        {active.resolvable && <th className="px-3 py-2" />}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {items.map(it => (
                        <tr key={it.id} className="hover:bg-gray-800/30">
                          {Object.keys(items[0])
                            .filter(k => k !== 'id' && !k.startsWith('_'))
                            .map(k => (
                              <td key={k} className="px-3 py-2 text-gray-300 align-top max-w-md">
                                {k.endsWith('_at') || k === 'created_at'
                                  ? <span className="whitespace-nowrap text-gray-400">{fmtDate(it[k])}</span>
                                  : it[k] === null || it[k] === ''
                                    ? <span className="text-gray-600">—</span>
                                    : <span className="break-words">{String(it[k])}</span>}
                              </td>
                            ))}
                          {it._open && (
                            <td className="px-3 py-2 whitespace-nowrap">
                              <button onClick={() => navigate(it._open)}
                                className="text-xs px-2 py-1 rounded bg-blue-700 hover:bg-blue-600 text-white">
                                Fix →
                              </button>
                            </td>
                          )}
                          {active.resolvable && (
                            <td className="px-3 py-2 whitespace-nowrap">
                              <button disabled={busy === it.id}
                                onClick={() => resolve(it.id, false)}
                                className="text-xs px-2 py-1 rounded bg-green-700 hover:bg-green-600 text-white disabled:opacity-50">
                                Done
                              </button>
                              {/* Dismiss writes stock_unresolved's status model; other
                                  resolvable sources record real-world work with Done. */}
                              {active.key === 'stock_unresolved' && (
                                <button disabled={busy === it.id}
                                  onClick={() => resolve(it.id, true)}
                                  className="ml-1 text-xs px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-200 disabled:opacity-50">
                                  Dismiss
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </PhoneCards>
              )}
              {active.resolvable && items.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  “Done” only records that a person dealt with it — it does not retry the
                  write. Whatever was wrong has to be fixed where it lives, or the retry
                  fails again or lands on the wrong article.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
